class IpinpinCartDrawer {
  constructor(drawer) {
    this.drawer = drawer;
    this.overlay = document.querySelector('[data-cart-drawer-overlay]');
    this.closeButton = drawer.querySelector('[data-cart-drawer-close]');
    this.content = drawer.querySelector('[data-cart-drawer-content]');
    this.itemsContainer = drawer.querySelector('[data-cart-drawer-items]');
    this.footer = drawer.querySelector('[data-cart-drawer-footer]');
    this.subtotal = drawer.querySelector('[data-cart-drawer-subtotal]');
    this.count = drawer.querySelector('[data-cart-drawer-count]');
    this.membership = drawer.querySelector('[data-cart-membership]');
    this.membershipInitialHtml = this.membership?.innerHTML || '';
    this.membershipInitialClass = this.membership?.className || '';
    this.membershipVariantId = Number(this.membership?.querySelector('[data-cart-membership-add]')?.dataset.variantId || 0);
    this.cartUrl = drawer.dataset.cartUrl || '/cart';
    this.rootUrl = window.Shopify?.routes?.root || drawer.dataset.rootUrl || '/';
    this.currency = window.Shopify?.currency?.active || 'USD';
    this.lastFocused = null;
    this.closeTimer = 0;
    this.bind();
    this.updateHeaderCount(Number(this.count?.textContent?.replace(/\D/g, '') || 0));
  }

  endpoint(path) {
    return `${this.rootUrl.replace(/\/$/, '')}/${path.replace(/^\//, '')}`;
  }

  bind() {
    document.querySelectorAll('.site-header a[href]').forEach((link) => {
      const path = new URL(link.href, window.location.origin).pathname.replace(/\/$/, '');
      if (path !== this.cartUrl.replace(/\/$/, '')) return;
      link.setAttribute('aria-controls', 'CartDrawer');
      link.setAttribute('aria-haspopup', 'dialog');
      link.addEventListener('click', (event) => {
        event.preventDefault();
        this.open(link);
      });
    });
    this.closeButton?.addEventListener('click', () => this.close());
    this.overlay?.addEventListener('click', () => this.close());
    this.content.addEventListener('click', (event) => this.handleContentClick(event));
    this.content.addEventListener('change', (event) => this.handleQuantityChange(event));
    document.addEventListener('keydown', (event) => this.handleKeydown(event));
    document.addEventListener('cart:refresh', () => {
      if (this.isOpen()) this.refresh();
    });
    document.addEventListener('cart:updated', () => this.open(null));
  }

  isOpen() {
    return this.drawer.classList.contains('is-open');
  }

  async open(trigger) {
    window.clearTimeout(this.closeTimer);
    if (trigger) this.lastFocused = trigger;
    this.overlay.hidden = false;
    this.drawer.inert = false;
    this.drawer.setAttribute('aria-hidden', 'false');
    document.body.classList.add('cart-drawer-open');
    window.requestAnimationFrame(() => {
      this.overlay.classList.add('is-open');
      this.drawer.classList.add('is-open');
    });
    this.closeButton?.focus();
    await this.refresh();
  }

  close() {
    this.drawer.classList.remove('is-open');
    this.overlay?.classList.remove('is-open');
    this.drawer.setAttribute('aria-hidden', 'true');
    this.drawer.inert = true;
    document.body.classList.remove('cart-drawer-open');
    this.closeTimer = window.setTimeout(() => {
      if (this.overlay) this.overlay.hidden = true;
    }, 300);
    this.lastFocused?.focus();
  }

  async refresh() {
    this.itemsContainer.setAttribute('aria-busy', 'true');
    try {
      const response = await fetch(this.endpoint('cart.js'), {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin'
      });
      if (!response.ok) throw new Error('Unable to load your cart.');
      const cart = await response.json();
      this.currency = cart.currency || this.currency;
      this.render(cart);
    } catch (error) {
      this.itemsContainer.innerHTML = `<div class="cart-drawer__error">${this.escape(error.message)}</div>`;
      this.footer.hidden = true;
    } finally {
      this.itemsContainer.removeAttribute('aria-busy');
    }
  }

  render(cart) {
    this.updateHeaderCount(cart.item_count);
    if (this.count) this.count.textContent = `(${cart.item_count})`;
    this.syncMembership(cart);
    if (!cart.items.length) {
      this.itemsContainer.innerHTML = `<div class="cart-drawer__empty"><div><strong>Your cart is empty</strong><a href="${this.endpoint('collections/all')}">Continue shopping</a></div></div>`;
      this.footer.hidden = true;
      return;
    }
    this.itemsContainer.innerHTML = `<div class="cart-drawer__items">${cart.items.map((item) => this.itemMarkup(item)).join('')}</div>`;
    if (this.subtotal) this.subtotal.textContent = this.money(cart.total_price);
    this.footer.hidden = false;
  }

  syncMembership(cart) {
    if (!this.membership || !this.membershipVariantId) return;
    const inCart = cart.items.some((item) => Number(item.variant_id) === this.membershipVariantId);
    if (inCart) {
      this.membership.className = `${this.membershipInitialClass} is-added`.trim();
      this.membership.innerHTML = '<span class="cart-drawer__membership-mark" aria-hidden="true">✓</span><div><strong>Membership added</strong><small>Complete checkout to purchase lifetime free shipping membership.</small></div>';
      return;
    }
    this.membership.className = this.membershipInitialClass;
    this.membership.innerHTML = this.membershipInitialHtml;
  }

  itemMarkup(item) {
    const imageUrl = item.featured_image?.url
      ? `${item.featured_image.url}${item.featured_image.url.includes('?') ? '&' : '?'}width=240`
      : '';
    const image = imageUrl
      ? `<img src="${this.escape(imageUrl)}" alt="${this.escape(item.featured_image.alt || item.product_title)}" loading="lazy">`
      : '';
    const variant = item.variant_title && item.variant_title !== 'Default Title'
      ? `<p class="cart-drawer__variant">${this.escape(item.variant_title)}</p>`
      : '<p class="cart-drawer__variant"></p>';
    const compare = item.original_line_price > item.final_line_price
      ? `<s>${this.money(item.original_line_price)}</s>`
      : '';
    return `<article class="cart-drawer__item" data-cart-line="${this.escape(item.key)}">
      <a class="cart-drawer__image" href="${this.escape(item.url)}">${image}</a>
      <div class="cart-drawer__details">
        <a class="cart-drawer__title" href="${this.escape(item.url)}">${this.escape(item.product_title)}</a>
        ${variant}
        <div class="cart-drawer__controls">
          <div class="cart-drawer__quantity" aria-label="Quantity">
            <button type="button" data-cart-quantity="-1" aria-label="Decrease quantity">−</button>
            <input type="number" min="0" value="${item.quantity}" data-cart-quantity-input aria-label="Quantity for ${this.escape(item.product_title)}">
            <button type="button" data-cart-quantity="1" aria-label="Increase quantity">+</button>
          </div>
          <button class="cart-drawer__remove" type="button" data-cart-remove>Remove</button>
        </div>
      </div>
      <div class="cart-drawer__price">${compare}<strong>${this.money(item.final_line_price)}</strong></div>
    </article>`;
  }

  handleContentClick(event) {
    const upsellButton = event.target.closest('[data-cart-upsell-add]');
    if (upsellButton) {
      this.addUpsell(upsellButton);
      return;
    }
    const fitTrack = this.drawer.querySelector('[data-cart-fit-track]');
    if (event.target.closest('[data-cart-fit-prev]')) {
      fitTrack?.scrollBy({ left: -(fitTrack.clientWidth * .78), behavior: 'smooth' });
      return;
    }
    if (event.target.closest('[data-cart-fit-next]')) {
      fitTrack?.scrollBy({ left: fitTrack.clientWidth * .78, behavior: 'smooth' });
      return;
    }
    const line = event.target.closest('[data-cart-line]');
    if (!line) return;
    if (event.target.closest('[data-cart-remove]')) {
      this.change(line, 0);
      return;
    }
    const quantityButton = event.target.closest('[data-cart-quantity]');
    if (!quantityButton) return;
    const input = line.querySelector('[data-cart-quantity-input]');
    const next = Math.max(0, Number(input.value) + Number(quantityButton.dataset.cartQuantity));
    this.change(line, next);
  }

  handleQuantityChange(event) {
    const input = event.target.closest('[data-cart-quantity-input]');
    const line = input?.closest('[data-cart-line]');
    if (!line) return;
    this.change(line, Math.max(0, Number(input.value) || 0));
  }

  async change(line, quantity) {
    line.classList.add('is-updating');
    try {
      const response = await fetch(this.endpoint('cart/change.js'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ id: line.dataset.cartLine, quantity })
      });
      const cart = await response.json();
      if (!response.ok) throw new Error(cart.description || 'Unable to update your cart.');
      this.render(cart);
      document.dispatchEvent(new CustomEvent('cart:changed', { detail: cart }));
    } catch (error) {
      line.classList.remove('is-updating');
      this.itemsContainer.insertAdjacentHTML('afterbegin', `<div class="cart-drawer__error">${this.escape(error.message)}</div>`);
    }
  }

  async addUpsell(button) {
    const original = button.textContent;
    const isMembership = button.matches('[data-cart-membership-add]');
    button.disabled = true;
    button.textContent = '…';
    try {
      const response = await fetch(this.endpoint('cart/add.js'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({
          items: [{
            id: Number(button.dataset.variantId),
            quantity: 1,
            ...(isMembership ? { properties: { _membership: 'Lifetime free shipping' } } : {})
          }]
        })
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.description || 'Unable to add this item.');
      button.textContent = '✓';
      await this.refresh();
      if (isMembership) {
        const membership = button.closest('[data-cart-membership]');
        if (membership) {
          membership.classList.add('is-added');
          membership.innerHTML = '<span class="cart-drawer__membership-mark" aria-hidden="true">✓</span><div><strong>Membership added</strong><small>Complete checkout to purchase lifetime free shipping membership.</small></div>';
        }
      }
    } catch (error) {
      button.textContent = '!';
      this.itemsContainer.insertAdjacentHTML('afterbegin', `<div class="cart-drawer__error">${this.escape(error.message)}</div>`);
    } finally {
      window.setTimeout(() => {
        if (isMembership || !button.isConnected) return;
        button.disabled = false;
        button.textContent = original;
      }, 1000);
    }
  }

  updateHeaderCount(count) {
    document.querySelectorAll('.site-header .header-actions a[href] > span[aria-hidden="true"]').forEach((element) => {
      const link = element.closest('a');
      if (new URL(link.href, window.location.origin).pathname.replace(/\/$/, '') === this.cartUrl.replace(/\/$/, '')) element.textContent = count;
    });
  }

  handleKeydown(event) {
    if (!this.isOpen()) return;
    if (event.key === 'Escape') {
      this.close();
      return;
    }
    if (event.key !== 'Tab') return;
    const focusable = [...this.drawer.querySelectorAll('a[href], button:not([disabled]), input:not([disabled])')].filter((element) => !element.closest('[hidden]'));
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  money(cents) {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency: this.currency }).format(Number(cents || 0) / 100);
  }

  escape(value) {
    return String(value ?? '').replace(/[&<>'"]/g, (character) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#39;', '"': '&quot;' })[character]);
  }
}

document.addEventListener('DOMContentLoaded', () => {
  const drawer = document.querySelector('[data-cart-drawer]');
  if (drawer && !drawer.dataset.initialized) {
    drawer.dataset.initialized = 'true';
    window.ipinpinCartDrawer = new IpinpinCartDrawer(drawer);
  }
});
